import ProductTypeApi, { ProductType } from '@/api/ProductTypeApi';
import { ProductTypeVariation } from '@/api/ProductTypeVariationApi';
import { Product } from '@/api/ProductApi';
import { UUID } from './types';
import { AttributeValue } from '@/api/AttributeValueApi';
import { Attribute } from '@/api/AttributeApi';
import {
	SerializedRenderer,
	ModelSlotsData,
	AccessorySlotsData,
	AccessorySlot,
} from '@/renderer/types';
import { Accessory } from '@/api/AccessoryApi';
import { Vector3Tuple } from 'three';

export enum CreatorStep {
	ATTRIBUTES,
	DIMENSION_ATTRIBUTES,
	ACCESSORIES,
	SUMMARY,
}

export interface CreatorOptions {
	holdInitialize: boolean;
	initialized: boolean;
	accessoriesDrawer: {
		opened: boolean;
		categoryId: UUID | null;
	};
	selectedCreatorAccessoryId: UUID | null;
	slotsData: ModelSlotsData;
	currentStep: CreatorStep;
	infoDrawer: null | {
		title: string;
		description: string;
		imageSrc?: string;
		code?: string;
	};
}

export interface CreatorRendererOptions {
	loading: boolean;
	distance: number;
	background: string;
}

export type AttributeWithValues = Attribute & {
	attributeValues: AttributeValue[];
};

export type AvailableAttributeValues = {
	attributes: AttributeWithValues[];
	dimensionAttributes: AttributeWithValues[];
};

export type AccessoryWithAvailability = Accessory & {
	disabled: boolean;
};

export interface CreatorAccessory {
	id?: UUID;
	refId: UUID;
	isColliding: boolean;
	isTemporary: boolean;
	accessory: Accessory;
	slotsData: AccessorySlotsData;
	selectedSlots: [
		AccessorySlot | null,
		AccessorySlot | null,
		AccessorySlot | null,
	];
	axesPositions: Vector3Tuple;
}

export interface CreatorState {
	product: Product | null;
	productType: ProductType | null;
	availableAccessories: AccessoryWithAvailability[];
	productTypeVariations: ProductTypeVariation[];
	productTypeVariation: ProductTypeVariation | null;
	availableAttributeValues: AvailableAttributeValues;
	selectedValues: Record<UUID, AttributeValue | null>;
	creatorAccessories: CreatorAccessory[];
	comment: string;
	quantity: number;
	renderer: CreatorRendererOptions;
	options: CreatorOptions;
}

export interface CreatorParams {
	selectedValues: Record<UUID, UUID | null>;
	creatorAccessories: (Omit<CreatorAccessory, 'accessory'> & {
		accessoryId: UUID;
	})[];
	exportedRenderer: SerializedRenderer;
	options: Partial<CreatorOptions>;
}

export type CreatorInitialData = Awaited<
	ReturnType<typeof ProductTypeApi.getInitialData>
>;
